// ============================================================================
// Casos de uso de WAYAK (CC-CONSOLE #1611 / #1612)
// ============================================================================
// Espejo de `apps/torre-de-control-api/functions/src/utils/useCases.js` (la
// prueba `useCases.parity.test.ts` cuida que coincidan). Cada caso de uso tiene
// sus propias licencias, su menú por omisión y sus objetivos de bienvenida:
//
//   • `transportista`       Operadores logísticos (las licencias de siempre)
//   • `freight-forwarder`   Agentes de carga, se cobra por usuario
//   • `monitoreo-dedicado`  Centrales de monitoreo, se cobra por unidad
//   • `pyme-flotilla`       PyMEs con flotilla propia, tarifa fija
//
//   • `companies.useCase`     → uno de la lista (omisión "transportista")
//   • `licenses.use_cases[]`  → en qué casos se vende (vacío = todos)
//   • `licenses.billing_unit` → "user" | "unit" (sólo per_unit)
//
// Los textos visibles (título y descripción) viven en i18n (`useCases`):
// `useCases:<id>.title` y `useCases:<id>.summary`.
// ============================================================================

export const USE_CASES = ["transportista", "freight-forwarder", "monitoreo-dedicado", "pyme-flotilla"] as const;
export type UseCase = (typeof USE_CASES)[number];
export const DEFAULT_USE_CASE: UseCase = "transportista";

export const BILLING_UNITS = ["user", "unit"] as const;
export type BillingUnit = (typeof BILLING_UNITS)[number];

export interface UseCasePreset {
  icon: string;
  /** Con qué se cobra su plan. */
  billingUnit: BillingUnit | "flat";
  /** Objetivos de la bienvenida (mismas claves que WAYAK_GOAL_APPS). */
  goals: readonly string[];
  /** Perfil de acceso por omisión (`access_profiles/{id}`); null = el de su rol. */
  accessProfileId: string | null;
}

const ALL_GOALS = [
  "telemetry",
  "fuel",
  "video",
  "trip_monitoring",
  "fleet_admin",
  "centinela_ai",
  "shipments",
  "reports",
] as const;

export const USE_CASE_PRESETS: Record<UseCase, UseCasePreset> = {
  transportista: {
    icon: "ri-truck-line",
    billingUnit: "user",
    goals: ALL_GOALS,
    // Gestión de Flota + la bolsa de Cargo Market, sólo para el creador (sin
    // techo de compañía), CC-CONSOLE#1639.
    accessProfileId: "caso-transportista",
  },
  "freight-forwarder": {
    icon: "ri-ship-2-line",
    billingUnit: "user",
    goals: ["shipments", "trip_monitoring", "telemetry", "reports"],
    accessProfileId: "caso-freight-forwarder",
  },
  "monitoreo-dedicado": {
    icon: "ri-shield-flash-line",
    billingUnit: "unit",
    goals: ["centinela_ai", "telemetry", "video", "reports"],
    accessProfileId: "caso-monitoreo-dedicado",
  },
  "pyme-flotilla": {
    icon: "ri-store-2-line",
    billingUnit: "flat",
    goals: ["telemetry", "fleet_admin", "shipments", "trip_monitoring", "fuel", "reports"],
    accessProfileId: "caso-pyme-flotilla",
  },
};

const clean = (value: unknown): string => (typeof value === "string" ? value.trim().toLowerCase() : "");

/** Caso de uso válido o null (lo que llega de una URL o de un formulario). */
export function parseUseCase(value: unknown): UseCase | null {
  const useCase = clean(value);
  return (USE_CASES as readonly string[]).includes(useCase) ? (useCase as UseCase) : null;
}

/** Caso de uso válido o el de omisión. */
export function normalizeUseCase(value: unknown): UseCase {
  return parseUseCase(value) ?? DEFAULT_USE_CASE;
}

/** `licenses.use_cases` sólo con valores de la lista, sin repetir y en orden. */
export function normalizeLicenseUseCases(value: unknown): UseCase[] {
  const list = Array.isArray(value) ? value : typeof value === "string" && value ? [value] : [];
  const wanted = new Set(list.map(clean));
  return USE_CASES.filter((useCase) => wanted.has(useCase));
}

/** ¿La licencia se vende en ese caso de uso? Sin `use_cases` vale para todos. */
export function licenseMatchesUseCase(license: { use_cases?: unknown } | null | undefined, useCase: unknown): boolean {
  const wanted = parseUseCase(useCase);
  if (!wanted) return true;
  const own = normalizeLicenseUseCases(license?.use_cases);
  return own.length === 0 || own.includes(wanted);
}

/** Planes de ese caso de uso (las genéricas incluidas). Sin caso de uso, todos. */
export function filterByUseCase<T extends { use_cases?: unknown }>(plans: readonly T[], useCase: unknown): T[] {
  return plans.filter((plan) => licenseMatchesUseCase(plan, useCase));
}

/**
 * Unidad de cobro de una licencia: "user" | "unit" para per_unit, "flat" para
 * tarifa fija y null para tiered. Sin `billing_unit` guardado: con escalones
 * por volumen es por usuario y sin ellos por unidad (como se pintaba antes).
 */
export function billingUnitOf(
  license: { billing_model?: string; billing_unit?: unknown; volume_tiers?: unknown } | null | undefined,
): BillingUnit | "flat" | null {
  const model = license?.billing_model;
  if (model === "flat") return "flat";
  if (model !== "per_unit") return null;
  const unit = clean(license?.billing_unit);
  if ((BILLING_UNITS as readonly string[]).includes(unit)) return unit as BillingUnit;
  return Array.isArray(license?.volume_tiers) && license.volume_tiers.length > 0 ? "user" : "unit";
}

/** Objetivos que se ofrecen a ese caso de uso. */
export function goalsForUseCase(useCase: unknown): string[] {
  return [...USE_CASE_PRESETS[normalizeUseCase(useCase)].goals];
}
